import type { GameSnapshot, Team } from "@msf/shared";
import { FlagIcon, PartyIcon, RematchIcon, SadClayIcon } from "./icons.js";

type GameOverReason = "normal" | "forfeit";

export function GameOverOverlay({
  snapshot,
  you,
  reason,
  rematchPendingBy,
  onRematch,
  onExit,
}: {
  snapshot: GameSnapshot;
  you: Team | null;
  myPlayerId: string | null;
  reason: GameOverReason | null;
  rematchPendingBy: Team | null;
  onRematch: () => void;
  onExit: () => void;
}) {
  const { winner, scores } = snapshot;
  const draw = winner === null;
  const youWon = you !== null && winner === you;
  const headline = draw
    ? "It's a draw!"
    : youWon
      ? "You win!"
      : you !== null
        ? "You lost"
        : `${winnerName(snapshot)} wins!`;
  const mood = youWon ? (
    <PartyIcon className="h-9 w-9" />
  ) : you !== null && !draw ? (
    <SadClayIcon className="h-9 w-9" />
  ) : null;
  const subtitle =
    reason === "forfeit"
      ? youWon
        ? "Your opponent left the game."
        : you !== null
          ? "You left the game."
          : "A player left the game."
      : null;

  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/45 p-4 backdrop-blur-sm">
      <div className="clay w-full max-w-sm p-6 text-center">
        {!draw && <FlagIcon team={winner} className="mx-auto h-16 w-16 animate-pop" />}
        <div className="mt-2 flex items-center justify-center gap-2">
          {mood && <span className="animate-pop">{mood}</span>}
          <h2 className="text-2xl font-black">{headline}</h2>
        </div>
        {subtitle && <p className="mt-1 text-sm opacity-60">{subtitle}</p>}

        <div className="mt-4 flex items-center justify-center gap-6">
          <Final team="red" name={seatName(snapshot, "red")} score={scores.red} win={winner === "red"} />
          <span className="text-lg font-black opacity-40">vs</span>
          <Final team="blue" name={seatName(snapshot, "blue")} score={scores.blue} win={winner === "blue"} />
        </div>

        <div className="mt-6 flex flex-col gap-2">
          {you !== null &&
            (() => {
              const iAsked = rematchPendingBy === you;
              const oppAsked = rematchPendingBy !== null && rematchPendingBy !== you;
              const label = iAsked ? "Waiting for opponent…" : oppAsked ? "Accept rematch" : "Rematch";
              return (
                <>
                  {oppAsked && <p className="text-sm font-bold opacity-70">Your opponent wants a rematch!</p>}
                  <button
                    type="button"
                    onClick={onRematch}
                    disabled={iAsked}
                    className="clay-btn clay-accent flex w-full items-center justify-center gap-1.5 py-3 font-extrabold disabled:opacity-60"
                  >
                    {!iAsked && <RematchIcon className="h-4 w-4" />}
                    {label}
                  </button>
                </>
              );
            })()}
          <button type="button" onClick={onExit} className="clay-btn w-full py-3 font-bold">
            Back to menu
          </button>
        </div>
      </div>
    </div>
  );
}

function seatName(snapshot: GameSnapshot, team: Team): string {
  return snapshot.players.find((p) => p.team === team)?.nickname ?? (team === "red" ? "Red" : "Blue");
}
function winnerName(snapshot: GameSnapshot): string {
  return snapshot.winner ? seatName(snapshot, snapshot.winner) : "Nobody";
}

function Final({ team, name, score, win }: { team: Team; name: string; score: number; win: boolean }) {
  const accent = team === "red" ? "#f2685f" : "#5aa9e6";
  return (
    <div className={`flex flex-col items-center ${win ? "" : "opacity-70"}`}>
      <FlagIcon team={team} className="h-8 w-8" />
      <span className="mt-1 max-w-20 truncate text-xs font-bold">{name}</span>
      <span className="text-3xl font-black" style={{ color: accent }}>
        {score}
      </span>
    </div>
  );
}
